/**
 * Table of Contents Block JavaScript - Full BEM Implementation
 */
document.addEventListener('DOMContentLoaded', function() {
    initTableOfContents();
});

/**
 * Check if a heading is inside an excluded block
 * @param {Element} heading - The heading element to check
 * @returns {boolean} - True if heading should be excluded
 */
function isHeadingInExcludedBlock(heading) {
    const excludedBlocks = [
        '.cta-block',
        '.hero-block',
        '.accordion-block',
        '.gallery-block',
        '.property-feed-block',
        '.post-feed-block',
        '.switchback-block',
        '.animation-block',
        '.travel-map-block',
        '.testimonials-block',
        '.modal-block',
        '.video-block',
        '.youtube-gallery-block',
        '.toc-block'
    ];
    
    return excludedBlocks.some(blockClass => heading.closest(blockClass));
}

function initTableOfContents() {
    const tocBlocks = document.querySelectorAll('.toc-block');
    
    tocBlocks.forEach(tocBlockElement => {
        const tocWrapper = tocBlockElement.querySelector('.toc-block__wrapper');
        const tocList = tocBlockElement.querySelector('.toc-block__list');
        
        if (!tocWrapper || !tocList) return;
        
        // Get settings from data attributes on the main block element
        const headingSelector = tocWrapper.dataset.headings || 'h2,h3';
        const smoothScroll = tocBlockElement.dataset.smoothScroll === 'true';
        const highlightActive = tocBlockElement.dataset.highlightActive === 'true';
        const isCollapsible = tocBlockElement.dataset.collapsible === 'true';
        
        // Find all headings only within text blocks
        const contentArea = document.querySelector('.entry-content, .post-content, main, .content');
        let headings = [];
        
        if (contentArea) {
            // Only select headings that are inside text blocks
            const textBlocks = contentArea.querySelectorAll('.text-block');
            console.log('Found', textBlocks.length, 'text blocks on page');
            
            textBlocks.forEach(textBlock => {
                const blockHeadings = textBlock.querySelectorAll(headingSelector);
                blockHeadings.forEach(heading => {
                    // Double check that heading is not in excluded areas
                    if (!heading.closest('.toc-block') && 
                        !heading.classList.contains('toc-block__title--exclude') &&
                        !heading.classList.contains('toc-exclude') &&
                        !isHeadingInExcludedBlock(heading)) {
                        headings.push(heading);
                    }
                });
            });
        }
        
        if (headings.length === 0) {
            // Hide TOC if no headings found
            tocBlockElement.style.display = 'none';
            return;
        }
        
        // Clear placeholder content
        tocList.innerHTML = '';
        
        // Generate TOC items
        generateTOCItems(tocList, headings);
        
        // Always set up accordion functionality (TOC is always collapsible)
        setupAccordionFunctionality(tocWrapper);
        
        // Always add click handlers for smooth scrolling (override setting)
        setupSmoothScrolling(tocList, highlightActive);
        
        // Set up scroll spy for active highlighting
        if (highlightActive) {
            setupScrollSpy(tocList, headings);
        }
        
        console.log('TOC initialized with', headings.length, 'headings from text blocks only');
    });
}

function generateTOCItems(tocList, headings) {
    let tocHTML = '';
    
    // Additional filter for any TOC-related headings that might have slipped through
    const filteredHeadings = headings.filter(heading => {
        const text = heading.textContent.trim().toLowerCase();
        // Ensure heading is within a text block and not in excluded areas
        return !text.includes('table of contents') && 
               !heading.classList.contains('toc-block__title') &&
               !heading.classList.contains('toc-block__title--exclude') &&
               !heading.classList.contains('toc-exclude') &&
               heading.closest('.text-block') && // Must be in a text block
               !isHeadingInExcludedBlock(heading); // Use helper function for excluded blocks
    });
    
    filteredHeadings.forEach((heading, index) => {
        const level = parseInt(heading.tagName.charAt(1));
        const text = heading.textContent.trim();
        const id = heading.id || generateId(text, index);
        
        // Add ID to heading if it doesn't have one
        if (!heading.id) {
            heading.id = id;
        }
        
        tocHTML += `<li class="toc-block__item toc-block__item--level-${level}">
            <a href="#${id}" class="toc-block__link" data-target="${id}">
                ${text}
            </a>
        </li>`;
    });
    
    tocList.innerHTML = tocHTML;
    
    console.log('Generated TOC with', filteredHeadings.length, 'headings (text blocks only, excluded TOC titles and other blocks)');
}

function setupAccordionFunctionality(tocBlock) {
    const header = tocBlock.querySelector('.toc-block__header');
    const content = tocBlock.querySelector('.toc-block__content');
    const block = tocBlock.closest('.toc-block');
    
    if (!header || !content) {
        console.log('TOC accordion elements not found, skipping accordion setup');
        return;
    }
    
    let isCollapsed = false;
    
    // Function to toggle accordion
    const toggleAccordion = () => {
        isCollapsed = !isCollapsed;
        
        // Update aria-expanded on header
        header.setAttribute('aria-expanded', (!isCollapsed).toString());
        
        // Update block data attribute for CSS
        block.setAttribute('data-collapsed', isCollapsed.toString());
        
        console.log('TOC toggled:', isCollapsed ? 'collapsed' : 'expanded');
    };
    
    // Make entire header clickable
    header.addEventListener('click', (e) => {
        e.preventDefault();
        toggleAccordion();
    });
    
    // Keyboard accessibility for header
    header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleAccordion();
        }
    });
    
    // Header is already set up in template with proper attributes
    // Ensure it starts expanded
    header.setAttribute('aria-expanded', 'true');
    block.setAttribute('data-collapsed', 'false');
    
    console.log('TOC accordion functionality initialized');
}

function setupSmoothScrolling(tocList, highlightActive) {
    const tocLinks = tocList.querySelectorAll('.toc-block__link');
    
    tocLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            // DON'T prevent default - let the browser handle the anchor linking
            // Just add active state handling
            if (highlightActive) {
                updateActiveLink(tocLinks, this);
            }
            
            // Add visual feedback
            const targetId = this.dataset.target;
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                setTimeout(() => {
                    targetElement.style.transition = 'background-color 0.3s ease';
                    targetElement.style.backgroundColor = 'rgba(59, 151, 222, 0.1)';
                    setTimeout(() => {
                        targetElement.style.backgroundColor = '';
                        setTimeout(() => {
                            targetElement.style.transition = '';
                        }, 300);
                    }, 1000);
                }, 100);
            }
        });
    });
}

function setupScrollSpy(tocList, headings) {
    const tocLinks = tocList.querySelectorAll('.toc-block__link');
    
    if (tocLinks.length === 0 || headings.length === 0) return;
    
    const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
    };
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const targetId = entry.target.id;
            const correspondingLink = tocList.querySelector(`[data-target="${targetId}"]`);
            
            if (entry.isIntersecting && correspondingLink) {
                updateActiveLink(tocLinks, correspondingLink);
            }
        });
    }, observerOptions);
    
    // Observe all headings
    headings.forEach(heading => {
        if (heading.id) {
            observer.observe(heading);
        }
    });
    
    // Set initial active state
    const firstLink = tocLinks[0];
    if (firstLink) {
        firstLink.classList.add('toc-block__link--active');
    }
}

function generateId(text, index) {
    // Create a URL-friendly ID from heading text
    let id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim('-'); // Remove leading/trailing hyphens
    
    // Ensure uniqueness
    id = id || `heading-${index}`;
    
    // Check if ID already exists and make it unique
    let counter = 1;
    let finalId = id;
    while (document.getElementById(finalId)) {
        finalId = `${id}-${counter}`;
        counter++;
    }
    
    return finalId;
}

function updateActiveLink(links, activeLink) {
    links.forEach(link => link.classList.remove('toc-block__link--active'));
    activeLink.classList.add('toc-block__link--active');
}

// Handle window resize
window.addEventListener('resize', function() {
    const stickyTocs = document.querySelectorAll('.toc-block--sticky');
    stickyTocs.forEach(toc => {
        // Recalculate sticky positioning if needed
        const tocWrapper = toc.querySelector('.toc-block__wrapper');
        if (tocWrapper && window.innerWidth < 768) {
            // Disable sticky on mobile
            tocWrapper.style.position = 'static';
        } else {
            tocWrapper.style.position = '';
        }
    });
});

// Export functions for potential external use
window.TOCBlock = {
    init: initTableOfContents,
    generateTOCItems: generateTOCItems,
    setupAccordionFunctionality: setupAccordionFunctionality,
    setupSmoothScrolling: setupSmoothScrolling,
    setupScrollSpy: setupScrollSpy
};